/// <amd-module name="Wit/Common/KanbanBoardSettings/Utils/SaveSettingsUtils" />
import { IBoardBadgeService } from "Agile/Board/Badge/Services/BoardBadgeService";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { getTFSProject } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { issueRequest } from "VSS/Platform/Fetch";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { areSwimlaneRulesNonEmpty } from "Wit/BoardsHub/Common/SwimlaneUtils";
import { attribute, BoardCardRuleSettings, BoardColumn, BoardRow, Rule } from "Wit/Clients/Work/Contracts/Work";
import { IWorkRestClient } from "Wit/Clients/Work/RestClient/Work";
import { IAnnotationSettingsData } from "Wit/Common/KanbanBoardSettings/Components/AnnotationSettingsView/AnnotationSettingsInterfaces";
import { getAnnotationsAsRules } from "Wit/Common/KanbanBoardSettings/Components/AnnotationSettingsView/AnnotationSettingsView";
import { ITestSettingsData, TestCreationOptions } from "Wit/Common/KanbanBoardSettings/Components/TestSettingsView/TestSettingsView";
import { getWiql } from "Wit/Common/PredicateWIT";
import { IBoardFieldSettings } from "Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettings";
import { getStyleSettingsAsStyleRules, IStyleService } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleService";
import { ITagStyleData } from "Wit/Common/WitSettingsViews/Components/TagStylesTable/TagStylesInterfaces";
import { getTagStyleSettingsAsRules } from "Wit/Common/WitSettingsViews/Components/TagStylesTable/TagStylesTable";
import { IColumnsSettings } from "../Components/BoardSettingsViews/ColumnSettings";
import { ISwimlanesSettings } from "../Components/BoardSettingsViews/SwimlanesSettings";

const testOutcomeSettingPath = "MS.VS.TestManagement/TestOutcomeSettings/Team/{0}";
const testPlanIdSettingPath = "TFSTests.Agile.Cards.TestAnnotation/Settings/TestPlan/{0}";
const cardReorderingKey = "cardReordering";

export async function saveBoardFieldSettings(
    pageContext: IVssPageContext,
    teamContext: TeamContext,
    boardId: string,
    boardFieldSettings: IBoardFieldSettings
): Promise<void> {
    const workClient = pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
    await workClient.updateBoardCardSettings(boardFieldSettings.getServerFieldSettings(), teamContext, boardId);
}

export async function saveCardStyleSettings(pageContext: IVssPageContext, teamContext: TeamContext, boardId: string): Promise<void> {
    const styleService = pageContext.getService<IStyleService>("IStyleService");
    return saveBoardRuleSettings(pageContext, teamContext, getStyleSettingsAsStyleRules(styleService.rows.value), "fill", boardId);
}

export async function saveTagStyleSettings(
    pageContext: IVssPageContext,
    teamContext: TeamContext,
    boardId: string,
    tagStyleData: ITagStyleData[]
): Promise<void> {
    return saveBoardRuleSettings(pageContext, teamContext, getTagStyleSettingsAsRules(tagStyleData), "tagStyle", boardId);
}

export async function saveAnnotationSettings(
    pageContext: IVssPageContext,
    teamContext: TeamContext,
    boardId: string,
    annotations: IAnnotationSettingsData[]
): Promise<void> {
    return saveBoardRuleSettings(pageContext, teamContext, getAnnotationsAsRules(annotations), "annotation", boardId);
}

export async function saveTestSettings(pageContext: IVssPageContext, teamId: string, testSettings: ITestSettingsData): Promise<void> {
    const sameTestOutcome = testSettings.sameTestOutcomes.value;
    const testPlanId =
        testSettings.testCreationOption.value === TestCreationOptions.SelectPlan && testSettings.testPlanId ? testSettings.testPlanId : "";
    const sameTestOutcomePath = format(testOutcomeSettingPath, teamId);
    const testPlanIdPath = format(testPlanIdSettingPath, teamId);
    const sameTestOutcomePromise = saveTestSetting(pageContext, sameTestOutcomePath, sameTestOutcome);
    const testPlanIdPromise = saveTestSetting(pageContext, testPlanIdPath, testPlanId);
    await Promise.all([sameTestOutcomePromise, testPlanIdPromise]);
}

export async function saveBoardColumnSettings(
    pageContext: IVssPageContext,
    teamContext: TeamContext,
    boardId: string,
    boardColumns: IColumnsSettings
): Promise<void> {
    // two different BoardColumn contracts that are generated from the same CS contract so this cast should be safe
    const updatedColumns = boardColumns.currentColumns as unknown as BoardColumn[];
    // Send empty string to the server, if column description is not set, same as on OWP
    updatedColumns.forEach(c => (c.description ??= ""));
    const workRestClient = pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
    await workRestClient.updateBoardColumns(updatedColumns, teamContext, boardId);
}

export async function saveBoardRowSettings(
    pageContext: IVssPageContext,
    teamContext: TeamContext,
    boardId: string,
    swimlaneSettings: ISwimlanesSettings
): Promise<void> {
    const updateRows = swimlaneSettings.swimlanes.value;
    const workRestClient = pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
    const savedRows = await workRestClient.updateBoardRows(updateRows, teamContext, boardId);

    await saveBoardRuleSettings(pageContext, teamContext, getSwimlaneRulesAsRules(swimlaneSettings, savedRows), "swimlaneRule", boardId);
    trackSwimlaneRulesUsage(pageContext, boardId, swimlaneSettings);
}

function getSwimlaneRulesAsRules(swimlaneSettings: ISwimlanesSettings, savedRows: BoardRow[]): Rule[] {
    const updateRows = swimlaneSettings.swimlanes.value;
    const tempIdsToRowIds = new Map<string, string>();
    for (const savedRow of savedRows) {
        const updateRow = updateRows.find(r => r.name === savedRow.name);
        if (updateRow && updateRow.id !== savedRow.id) {
            tempIdsToRowIds.set(updateRow.id, savedRow.id);
        }
    }
    const swimlaneRules = swimlaneSettings.swimlanesRules.value;
    return Object.entries(swimlaneRules)
        .map(([rowId, rules]) => {
            const actualRowId = tempIdsToRowIds.get(rowId) ?? rowId;
            const name = updateRows.find(r => r.id === rowId)?.name;
            if (!actualRowId || !name || !rules) {
                return undefined;
            }
            return {
                name: updateRows.find(r => r.id === rowId)?.name ?? "",
                isEnabled: true.toString(),
                filter: getWiql({ groups: [], clauses: rules.filterClauses, maxGroupLevel: 0 }),
                clauses: rules.filterClauses,
                settings: { rowId: actualRowId } as attribute
            };
        })
        .filter((rule): rule is Rule => rule !== undefined);
}

function trackSwimlaneRulesUsage(pageContext: IVssPageContext, boardId: string, swimlaneSettings: ISwimlanesSettings): void {
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    telemetryService.publishEvent("SwimlaneRules", "SavedSwimlaneRules", {
        boardId: boardId,
        originalSwimlaneWithRulesCount: Object.values(swimlaneSettings.originalSwimlaneRules).filter(areSwimlaneRulesNonEmpty).length,
        newSwimlaneWithRulesCount: Object.values(swimlaneSettings.swimlanesRules.value).filter(areSwimlaneRulesNonEmpty).length
    });
}

export async function saveCardReorderSettings(
    pageContext: IVssPageContext,
    teamContext: TeamContext,
    boardId: string,
    preserveBacklogOrder: boolean
): Promise<void> {
    const workRestClient = pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");

    // API only accepts 1 or 0 to specify the option you are setting
    const boardOptions = { [cardReorderingKey]: preserveBacklogOrder ? "1" : "0" };
    await workRestClient.setBoardOptions(boardOptions, teamContext, boardId);
}

export async function saveStatusBadgeSettings(
    pageContext: IVssPageContext,
    projectId: string,
    teamId: string,
    boardId: string,
    isStatusBadgePublic: boolean
): Promise<void> {
    const badgeService = pageContext.getService<IBoardBadgeService>("IBoardBadgeService");
    await badgeService.setIsPublicOption(projectId, teamId, boardId, isStatusBadgePublic);
}

async function saveTestSetting(pageContext: IVssPageContext, settingPath: string, settingValue: string | number | boolean): Promise<void> {
    const hostPath = pageContext.getService<IVssPageService>("IVssPageService").getData()?.hostPath ?? "";
    const project = getTFSProject(pageContext)?.id ?? "";
    const requestUrl = combineUrlPaths(encodeURI(hostPath), `${project}/_api/_webSettings/setString?__v=5`);

    // this endpoint is waaaay old and it isn't possible to generate a rest client for it
    // so we have to do this
    await issueRequest(requestUrl, {
        method: "POST",
        body: JSON.stringify({ path: settingPath, value: settingValue, scope: 4 /** project */ }),
        headers: {
            "Content-Type": "application/json"
        }
    });
}

async function saveBoardRuleSettings(
    pageContext: IVssPageContext,
    teamContext: TeamContext,
    rules: Rule[],
    ruleType: string,
    boardId: string
): Promise<void> {
    const boardCardRuleSettings = {
        rules: {
            [ruleType]: rules
        }
    } as BoardCardRuleSettings;

    const workClient = pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
    await workClient.updateBoardCardRuleSettings(boardCardRuleSettings, teamContext, boardId);
}
